import { ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { clsx } from '@mantine/styles'

import { Avatar, Text, Tooltip } from '@percent/lemonade'
import classes from './SearchResultCardLayout.module.scss'

type SearchResultCardLayoutProps = Readonly<{
  children: ReactNode
  title: string
  linkTo?: string
  handleTracking?: VoidFunction
  image?: string | null
  logo?: string | null
  logoTooltipText?: string | null
  logoBadge?: string | null
  logoBadgeTooltip?: string | null
  additionalClassName?: string
}>

function ContentWrapper({ linkTo, handleTracking, children }: Partial<SearchResultCardLayoutProps>) {
  if (linkTo) {
    return (
      <Link className={classes.LinkContentWrapper} to={linkTo} onClick={() => handleTracking?.()}>
        {children}
      </Link>
    )
  }

  return <div className={classes.RegularContentWrapper}>{children}</div>
}
export function SearchResultCardLayout({
  title,
  children,
  linkTo,
  handleTracking,
  image,
  logo,
  logoTooltipText,
  logoBadge,
  logoBadgeTooltip,
  additionalClassName
}: SearchResultCardLayoutProps) {
  const titleRef = useRef<HTMLDivElement | null>(null)
  const [isTitleOverflowing, setIsTitleOverflowing] = useState(false)

  useLayoutEffect(() => {
    if (titleRef.current) {
      setIsTitleOverflowing(titleRef.current.scrollHeight > titleRef.current.clientHeight)
    }
  }, [title])

  const titleNode = (
    <div ref={titleRef} className={classes.Title}>
      <Text as="p" fontWeight="semiBold">
        {title}
      </Text>
    </div>
  )

  return (
    <div
      role="listitem"
      data-testid="search-result-card"
      className={clsx(classes.OuterWrapper, additionalClassName, { [classes.OuterWrapperBordered]: !image })}
    >
      <ContentWrapper linkTo={linkTo} handleTracking={handleTracking}>
        {image ? (
          <div className={classes.Header} style={{ backgroundImage: `url(${image})` }}>
            {logo && (
              <div className={classes.BannerLogo}>
                <Avatar
                  image={logo}
                  alt={title}
                  shape="oval"
                  border
                  badge={logoBadge}
                  badgeTooltip={logoBadgeTooltip}
                  tooltip={logoTooltipText}
                  dataTestId="search-result-card-logo"
                  ariaLabel={logoTooltipText || undefined}
                />
              </div>
            )}
          </div>
        ) : (
          logo && (
            <div className={classes.StaticLogo}>
              <Avatar
                image={logo}
                alt={title}
                size="large"
                shape="oval"
                border
                badge={logoBadge}
                badgeTooltip={logoBadgeTooltip}
                tooltip={logoTooltipText}
                dataTestId="search-result-card-logo"
                ariaLabel={logoTooltipText || undefined}
              />
            </div>
          )
        )}
        {isTitleOverflowing ? (
          <Tooltip content={title} placement="top">
            {titleNode}
          </Tooltip>
        ) : (
          titleNode
        )}
        {children}
      </ContentWrapper>
    </div>
  )
}
